import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import SelectedLocation from '../interfaces/SelectedLocation';
import {reverseGeocode} from '../api/geocoding';

const STORAGE_KEY = 'selectedLocation';

interface LocationContextType {
    location: SelectedLocation | null;
    setLocation: (loc: SelectedLocation | null) => void;
    isLocating: boolean;
    requestCurrentLocation: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType>({
    location: null,
    setLocation: () => {},
    isLocating: false,
    requestCurrentLocation: async () => {},
});

export const useLocationContext = () => useContext(LocationContext);

interface LocationProviderProps {
    children: React.ReactNode;
}

const readStoredLocation = (): SelectedLocation | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.lat === 'number' && typeof parsed?.lon === 'number' && typeof parsed?.label === 'string') {
            return parsed as SelectedLocation;
        }
    } catch {
        // ignore corrupted storage
    }
    return null;
};

export const LocationProvider: React.FC<LocationProviderProps> = ({children}) => {
    const [location, setLocationState] = useState<SelectedLocation | null>(() => readStoredLocation());
    const [isLocating, setIsLocating] = useState(false);

    const setLocation = useCallback((loc: SelectedLocation | null) => {
        setLocationState(loc);
        if (loc) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const requestCurrentLocation = useCallback(async () => {
        if (!navigator.geolocation) {
            throw new Error('Geolocation is not supported in this browser.');
        }
        setIsLocating(true);
        try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
            });
            const { latitude, longitude } = position.coords;
            const label = await reverseGeocode(latitude, longitude);
            setLocation({ label, lat: latitude, lon: longitude, source: 'current' });
        } finally {
            setIsLocating(false);
        }
    }, [setLocation]);

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY) {
                setLocationState(readStoredLocation());
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        if (location) return;
        if (typeof navigator === 'undefined' || !navigator.geolocation) return;
        if (sessionStorage.getItem('autoLocationAttempted')) return;
        sessionStorage.setItem('autoLocationAttempted', '1');
        requestCurrentLocation().catch(() => {
            // User denied permission or geolocation failed — keep location null silently.
            // They can still use the picker manually.
        });
    }, []); // intentionally run once on mount

    return (
        <LocationContext.Provider value={{location, setLocation, isLocating, requestCurrentLocation}}>
            {children}
        </LocationContext.Provider>
    );
};

export default LocationProvider;
